import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, post } from "../../../utility/fetch";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(2),
  borderRadius: "12px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "8px",
  textTransform: "none",
  padding: "12px 24px",
  fontWeight: 600,
}));

const AddPatients = () => {
  // Get user info from localStorage for clinicId
  const userInfo = JSON.parse(localStorage.getItem("USER_INFO"));

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    email: "",
    phoneNumber: "",
    stateOfOrigin: "",
    lga: "",
    placeOfBirth: "",
    maritalStatus: "",
    nationality: "",
    clinicId: userInfo?.healthCareProviderId || "",
    pictureUrl: null,
    nin: "",
    hasHmo: false,
    hasRetainership: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingNationalities, setLoadingNationalities] = useState(false);

  // Fetch states from API
  const fetchStates = async () => {
    setLoadingStates(true);
    try {
      const response = await get("profile/state/list/1/100", true);
      if (response && response.data && response.data.states) {
        setStates(response.data.states);
      } else {
        // Fallback states
        setStates([
          { stateId: 1, stateName: "Lagos" },
          { stateId: 2, stateName: "Abuja" },
          { stateId: 3, stateName: "Edo" },
          { stateId: 4, stateName: "Oyo" },
          { stateId: 5, stateName: "Rivers" },
        ]);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      setStates([
        { stateId: 1, stateName: "Lagos" },
        { stateId: 2, stateName: "Abuja" },
        { stateId: 3, stateName: "Edo" },
        { stateId: 4, stateName: "Oyo" },
        { stateId: 5, stateName: "Rivers" },
      ]);
    } finally {
      setLoadingStates(false);
    }
  };

  // Fetch nationalities from API
  const fetchNationalities = async () => {
    setLoadingNationalities(true);
    try {
      const response = await get("profile/nationality/list/1/100", true);
      if (response && response.data && response.data.nationalities) {
        setNationalities(response.data.nationalities);
      } else {
        // Fallback nationalities
        setNationalities([
          { nationalityId: 1, nationalityName: "Nigerian" },
          { nationalityId: 2, nationalityName: "American" },
          { nationalityId: 3, nationalityName: "British" },
          { nationalityId: 4, nationalityName: "Australian" },
        ]);
      }
    } catch (error) {
      console.error("Error fetching nationalities:", error);
      setNationalities([
        { nationalityId: 1, nationalityName: "Nigerian" },
        { nationalityId: 2, nationalityName: "American" },
        { nationalityId: 3, nationalityName: "British" },
        { nationalityId: 4, nationalityName: "Australian" },
      ]);
    } finally {
      setLoadingNationalities(false);
    }
  };

  useEffect(() => {
    fetchStates();
    fetchNationalities();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.length < 2) {
      newErrors.firstName = "First name must be at least 2 characters";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.length < 2) {
      newErrors.lastName = "Last name must be at least 2 characters";
    }

    if (!formData.gender) {
      newErrors.gender = "Gender is required";
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = "Date of birth is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else {
      const phoneRegex = /^[0-9+\-\s()]{10,}$/;
      if (!phoneRegex.test(formData.phoneNumber)) {
        newErrors.phoneNumber = "Please enter a valid phone number";
      }
    }

    if (!formData.stateOfOrigin) {
      newErrors.stateOfOrigin = "State of origin is required";
    }

    if (!formData.nationality) {
      newErrors.nationality = "Nationality is required";
    }

    if (!formData.nin.trim()) {
      newErrors.nin = "NIN is required";
    } else if (formData.nin.length !== 11) {
      newErrors.nin = "NIN must be exactly 11 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Format date from YYYY-MM-DD to DD-MM-YYYY
      const formatDateToDDMMYYYY = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      // Prepare final data for submission
      const finalData = {
        ...formData,
        dateOfBirth: formatDateToDDMMYYYY(formData.dateOfBirth),
        clinicId: parseInt(formData.clinicId) || 2,
      };

      console.log("Submitting patient data:", finalData);

      // Post to /patients/AddPatient endpoint
      const response = await post("/patients/AddPatient", finalData, true);

      if (response && response.status === 200) {
        alert("Patient added successfully!");
        handleReset();
      } else {
        console.error("Failed to add patient:", response);
        if (response && response.data && response.data.errors) {
          // Handle API validation errors
          const apiErrors = {};
          response.data.errors.forEach((error) => {
            const field = error.field?.toLowerCase();
            if (field && !apiErrors[field]) {
              apiErrors[field] = error.message;
            }
          });
          setErrors(apiErrors);
        } else {
          alert("Failed to add patient. Please try again.");
        }
      }
    } catch (error) {
      console.error("Error adding patient:", error);
      alert("An error occurred while adding the patient. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      firstName: "",
      lastName: "",
      gender: "",
      dateOfBirth: "",
      email: "",
      phoneNumber: "",
      stateOfOrigin: "",
      lga: "",
      placeOfBirth: "",
      maritalStatus: "",
      nationality: "",
      clinicId: userInfo?.healthCareProviderId || "",
      pictureUrl: null,
      nin: "",
      hasHmo: false,
      hasRetainership: false,
    });
    setErrors({});
  };

  return (
    <Box sx={{ maxWidth: 1000, margin: "0 auto", padding: 2 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ color: "#2E7D32", fontWeight: 600 }}
      >
        Add New Patient
      </Typography>

      <StyledPaper>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ color: "#1976D2", marginBottom: 3 }}
        >
          Patient Information
        </Typography>

        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="First Name *"
              value={formData.firstName}
              onChange={(e) => handleInputChange("firstName", e.target.value)}
              error={!!errors.firstName}
              helperText={errors.firstName}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Last Name *"
              value={formData.lastName}
              onChange={(e) => handleInputChange("lastName", e.target.value)}
              error={!!errors.lastName}
              helperText={errors.lastName}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.gender}>
              <InputLabel>Gender *</InputLabel>
              <Select
                value={formData.gender}
                onChange={(e) => handleInputChange("gender", e.target.value)}
                label="Gender *"
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
              {errors.gender && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {errors.gender}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Date of Birth *"
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => handleInputChange("dateOfBirth", e.target.value)}
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email *"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone Number *"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.stateOfOrigin}>
              <InputLabel>State of Origin *</InputLabel>
              <Select
                value={formData.stateOfOrigin}
                onChange={(e) =>
                  handleInputChange("stateOfOrigin", e.target.value)
                }
                label="State of Origin *"
                disabled={loadingStates}
              >
                {states.map((state) => (
                  <MenuItem key={state.stateId} value={state.stateName}>
                    {state.stateName}
                  </MenuItem>
                ))}
              </Select>
              {errors.stateOfOrigin && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {errors.stateOfOrigin}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="LGA"
              value={formData.lga}
              onChange={(e) => handleInputChange("lga", e.target.value)}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Place of Birth"
              value={formData.placeOfBirth}
              onChange={(e) =>
                handleInputChange("placeOfBirth", e.target.value)
              }
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Marital Status</InputLabel>
              <Select
                value={formData.maritalStatus}
                onChange={(e) =>
                  handleInputChange("maritalStatus", e.target.value)
                }
                label="Marital Status"
              >
                <MenuItem value="Single">Single</MenuItem>
                <MenuItem value="Married">Married</MenuItem>
                <MenuItem value="Divorced">Divorced</MenuItem>
                <MenuItem value="Widowed">Widowed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.nationality}>
              <InputLabel>Nationality *</InputLabel>
              <Select
                value={formData.nationality}
                onChange={(e) =>
                  handleInputChange("nationality", e.target.value)
                }
                label="Nationality *"
                disabled={loadingNationalities}
              >
                {nationalities.map((nationality) => (
                  <MenuItem
                    key={nationality.nationalityId}
                    value={nationality.nationalityName}
                  >
                    {nationality.nationalityName}
                  </MenuItem>
                ))}
              </Select>
              {errors.nationality && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {errors.nationality}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="NIN *"
              value={formData.nin}
              onChange={(e) => handleInputChange("nin", e.target.value)}
              error={!!errors.nin}
              helperText={errors.nin}
              variant="outlined"
              inputProps={{ maxLength: 11 }}
            />
          </Grid>

          {/* HMO and Retainership Options */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasHmo}
                    onChange={(e) =>
                      handleInputChange("hasHmo", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Has HMO"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hasRetainership}
                    onChange={(e) =>
                      handleInputChange("hasRetainership", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="Has Retainership"
              />
            </Box>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
            marginTop: 4,
          }}
        >
          <StyledButton
            variant="outlined"
            onClick={handleReset}
            disabled={loading}
            sx={{ color: "#666", borderColor: "#666" }}
          >
            Reset Form
          </StyledButton>
          <StyledButton
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              backgroundColor: "#2E7D32",
              "&:hover": { backgroundColor: "#1B5E20" },
            }}
          >
            {loading ? (
              <>
                <CircularProgress
                  size={20}
                  sx={{ marginRight: 1, color: "white" }}
                />
                Adding Patient...
              </>
            ) : (
              "Add Patient"
            )}
          </StyledButton>
        </Box>
      </StyledPaper>
    </Box>
  );
};

export default AddPatients;
